import type {
  BotSkill,
  CardId,
  ClientMessage,
  FriendView,
  GameAction,
  Identity,
  PlayerPreferences,
  RoomConfig,
  RoomCrest,
  RoomPreview,
  RoomSummary,
  RoomView,
  ServerMessage,
  TableView,
} from '@rummy/protocol';
import { interactionMetrics, type InteractionName } from '../feedback/interactionMetrics.ts';

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'offline';

/** A server-confirmed draw that is waiting for the complete table snapshot. */
export interface PendingDraw {
  id: string;
  handId: string;
  source: Extract<GameAction, { type: 'draw' }>['source'];
  /** Null until the server has accepted the draw and revealed it to its owner. */
  card: CardId | null;
}

/** A move that has left the input device but has not reached a fresh table
 * projection yet. It is a presentation cue, never optimistic game state. */
export interface PendingAction {
  id: string;
  type: Exclude<GameAction['type'], 'arrange'>;
  startedAt: number;
}

/** Server retry window (§8.1) — the seat is held for the same duration. */
export const RECONNECT_GRACE_MS = 120_000;

export interface AppState {
  connection: ConnectionState;
  /** Epoch ms of the first drop in the current outage; 0 while connected.
   *  Paired with `RECONNECT_GRACE_MS`, this is what lets the UI show a
   *  countdown instead of just "reconnecting" with no sense of urgency. */
  disconnectedAt: number;
  identity: Identity | null;
  region: string;
  friends: FriendView[];
  rooms: RoomSummary[];
  room: RoomView | null;
  preview: RoomPreview | null;
  table: TableView | null;
  pendingDraw: PendingDraw | null;
  pendingAction: PendingAction | null;
  seq: number;
  error: { code: string; at: number } | null;
  invite: { from: string; roomId: string; roomLabel: string; at: number } | null;
}

const INITIAL: AppState = {
  connection: 'connecting',
  disconnectedAt: 0,
  identity: null,
  region: '',
  friends: [],
  rooms: [],
  room: null,
  preview: null,
  table: null,
  pendingDraw: null,
  pendingAction: null,
  seq: 0,
  error: null,
  invite: null,
};

const TOKEN_KEY = 'rummy.token';
const PROFILE_KEY = 'rummy.profile';

/**
 * Transport (§5, §8).
 *
 * The client is a renderer and an input device. It holds no authoritative
 * state, which is why reconnection is trivial: on resync it discards
 * everything local and rebuilds from the server's snapshot. There is no merge.
 */
export class GameClient {
  private socket: WebSocket | null = null;
  private state: AppState = INITIAL;
  private listeners = new Set<() => void>();
  private attempt = 0;
  private disconnectedAt = 0;
  /** Whether a socket has ever opened, which separates "the connection dropped"
   *  from "the server was never there". */
  private everOpen = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private queue: ClientMessage[] = [];
  private pendingRoom: { id: string; mode: 'join' | 'spectate' } | null = null;

  constructor(private readonly url: string) {}

  // ------------------------------------------------------------ subscription

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AppState => this.state;

  private set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  // -------------------------------------------------------------- connection

  connect(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;
    this.set({ connection: this.attempt === 0 ? 'connecting' : this.state.connection });

    const socket = new WebSocket(this.url);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.disconnectedAt = 0;
      this.everOpen = true;
      this.set({ connection: 'open', disconnectedAt: 0 });
      const profile = readProfile();
      this.send({
        t: 'hello',
        token: localStorage.getItem(TOKEN_KEY),
        locale: navigator.language,
        ...(profile.handle ? { handle: profile.handle } : {}),
        ...(profile.avatar ? { avatar: profile.avatar } : {}),
      });
      for (const message of this.queue.splice(0)) this.send(message);
      if (this.pendingRoom) {
        this.send(
          this.pendingRoom.mode === 'spectate'
            ? { t: 'spectate-room', roomId: this.pendingRoom.id }
            : { t: 'join-room', roomId: this.pendingRoom.id },
        );
      }
      this.pingTimer = setInterval(() => this.send({ t: 'ping' }), 20_000);
    };

    socket.onmessage = (event) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }
      this.receive(message);
    };

    socket.onclose = () => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.socket = null;
      // The in-flight request did not receive a confirmation. A later resync
      // is authoritative, so never leave a phantom card on the tray.
      if (this.state.pendingAction) interactionMetrics.settle(this.state.pendingAction.id, 'abandoned');
      this.set({ pendingDraw: null, pendingAction: null });
      this.scheduleReconnect();
    };

    socket.onerror = () => socket.close();
  }

  /** §8.1 — exponential backoff for 120 seconds, then a slow retry. */
  private scheduleReconnect(): void {
    if (this.disconnectedAt === 0) this.disconnectedAt = Date.now();
    const elapsed = Date.now() - this.disconnectedAt;
    const givenUp = elapsed > RECONNECT_GRACE_MS;
    // "Lost the connection" is the wrong thing to tell someone whose first
    // socket never opened — nothing was lost, the server is not there. Both
    // cases keep retrying; they differ only in what the player is told.
    this.set({
      connection: givenUp || !this.everOpen ? 'offline' : 'reconnecting',
      disconnectedAt: this.disconnectedAt,
    });

    const delay = givenUp
      ? 10_000
      : Math.min(8_000, 400 * 2 ** this.attempt) + Math.random() * 250;
    this.attempt++;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  /**
   * Try again now instead of waiting out the backoff.
   *
   * The backoff is right for a network that dropped and will come back. It is
   * wrong for "the server was not running when I opened the tab", where the
   * player fixes the problem themselves and then has to sit through a delay
   * that is counting up from their earlier failures.
   */
  retry(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.attempt = 0;
    this.disconnectedAt = 0;
    this.connect();
  }

  /**
   * The stored token changed (sign-in, registration, sign-out). Drop the
   * socket and open a fresh one, which says hello with the new token and
   * rebuilds all state from the server's snapshot — the reconnection path,
   * reused on purpose.
   */
  reidentify(): void {
    this.attempt = 0;
    this.disconnectedAt = 0;
    this.set({ identity: null, room: null, table: null, pendingDraw: null, pendingAction: null, friends: [], rooms: [] });
    if (this.socket) {
      this.socket.close();
    } else {
      this.connect();
    }
  }

  private receive(message: ServerMessage): void {
    switch (message.t) {
      case 'welcome':
        localStorage.setItem(TOKEN_KEY, message.token);
        writeProfile({ handle: message.identity.handle, avatar: message.identity.avatar });
        this.set({ identity: message.identity, region: message.region });
        return;
      case 'lobby':
        this.set({ friends: message.friends, rooms: message.rooms });
        return;
      case 'room':
        if (message.room === null) this.pendingRoom = null;
        this.set({
          room: message.room,
          seq: message.seq,
          ...(message.room === null ? { table: null, pendingDraw: null, pendingAction: null } : {}),
        });
        return;
      case 'room-preview':
        this.set({ preview: message.preview });
        return;
      case 'table':
        // Snapshot replace, never merge (§8.4).
        this.set({
          table: message.view,
          seq: message.seq,
          ...(this.shouldClearPendingDraw(message.view) ? { pendingDraw: null } : {}),
        });
        return;
      case 'draw-ack': {
        const pending = this.state.pendingDraw;
        // UUID + hand id make an old acknowledgement harmless after a resync
        // or a new deal. The snapshot remains the sole source of truth.
        if (pending?.id === message.id && pending.handId === message.handId) {
          this.set({ pendingDraw: { ...pending, card: message.card } });
        }
        return;
      }
      case 'action-ack': {
        const pending = this.state.pendingAction;
        if (pending?.id === message.id) {
          interactionMetrics.settle(message.id, 'confirmed');
          this.set({ pendingAction: null });
        }
        return;
      }
      case 'notification':
        this.set({
          invite: {
            from: message.from.handle,
            roomId: message.roomId,
            roomLabel: message.roomLabel,
            at: Date.now(),
          },
        });
        return;
      case 'error':
        if (this.state.pendingAction) interactionMetrics.settle(this.state.pendingAction.id, 'rejected');
        this.set({ error: { code: message.code, at: Date.now() }, pendingDraw: null, pendingAction: null });
        return;
      case 'pong':
        return;
    }
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    // Navigation and profile intentions can safely wait for a connection. A
    // game move cannot: its meaning depends on the exact table snapshot that
    // was visible when it was made, so replaying it after reconnect can turn a
    // stale tap into an unintended legal move.
    } else if (message.t !== 'ping' && message.t !== 'action' && message.t !== 'resync') {
      this.queue.push(message);
    }
  }

  // ----------------------------------------------------------------- actions

  setProfile(handle: string, avatar: string): void {
    writeProfile({ handle, avatar });
    this.send({ t: 'set-profile', handle, avatar });
  }

  setPreferences(preferences: PlayerPreferences): void {
    this.send({ t: 'set-preferences', preferences });
  }

  createRoom(config?: Partial<RoomConfig>): void {
    this.send(config ? { t: 'create-room', config } : { t: 'create-room' });
  }

  joinRoom(roomId: string): void {
    this.pendingRoom = { id: roomId, mode: 'join' };
    this.send({ t: 'join-room', roomId });
  }

  previewRoom(roomId: string): void {
    this.send({ t: 'preview-room', roomId });
  }

  spectateRoom(roomId: string): void {
    this.pendingRoom = { id: roomId, mode: 'spectate' };
    this.send({ t: 'spectate-room', roomId });
  }

  leaveRoom(): void {
    this.pendingRoom = null;
    this.send({ t: 'leave-room' });
  }

  dismissPreview(): void {
    this.set({ preview: null });
  }

  configureRoom(patch: Partial<RoomConfig>): void {
    this.send({ t: 'configure-room', patch });
  }

  renameRoom(name: string): void {
    this.send({ t: 'rename-room', name });
  }

  setRoomCrest(crest: RoomCrest): void {
    this.send({ t: 'set-room-crest', crest });
  }

  addBot(character?: string, skill?: BotSkill): void {
    this.send({ t: 'add-bot', ...(character ? { character } : {}), ...(skill ? { skill } : {}) });
  }

  fillSeats(skill?: BotSkill): void {
    this.send({ t: 'fill-seats', ...(skill ? { skill } : {}) });
  }

  setBotSkill(accountId: string, skill: BotSkill): void {
    this.send({ t: 'set-bot-skill', accountId, skill });
  }

  removeSeat(accountId: string): void {
    this.send({ t: 'remove-seat', accountId });
  }

  startSession(): void {
    this.send({ t: 'start-session' });
  }

  rematch(): void {
    this.send({ t: 'rematch' });
  }

  continueSession(): void {
    this.send({ t: 'continue' });
  }

  invite(accountId: string): void {
    this.send({ t: 'invite', accountId });
  }

  emote(key: string): void {
    this.send({ t: 'emote', key });
  }

  /** §5 — every action carries a UUID and the client's last-seen sequence. */
  act(action: GameAction): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    const id = crypto.randomUUID();
    if (action.type === 'draw') {
      this.set({
        pendingDraw: {
          id,
          handId: this.state.table?.handId ?? '',
          source: action.source,
          card: null,
        },
      });
    }
    if (action.type !== 'arrange') {
      interactionMetrics.begin(id, action.type as InteractionName);
      this.set({ pendingAction: { id, type: action.type, startedAt: performance.now() } });
    }
    this.send({ t: 'action', id, lastSeq: this.state.seq, action });
  }

  private shouldClearPendingDraw(view: TableView): boolean {
    const pending = this.state.pendingDraw;
    if (!pending) return false;
    if (pending.handId !== view.handId) return true;
    const hand = view.you?.hand ?? [];
    return (
      (pending.card !== null && hand.includes(pending.card)) ||
      (view.turn === this.state.identity?.id &&
        view.awaiting === 'discard' &&
        hand.length > view.rules.handSize)
    );
  }

  resync(): void {
    this.send({ t: 'resync', lastSeq: this.state.seq });
  }

  clearError(): void {
    if (this.state.error) this.set({ error: null });
  }

  dismissInvite(): void {
    this.set({ invite: null });
  }
}

interface Profile {
  handle?: string;
  avatar?: string;
}

function readProfile(): Profile {
  try {
    return JSON.parse(localStorage.getItem(PROFILE_KEY) ?? '{}') as Profile;
  } catch {
    return {};
  }
}

function writeProfile(profile: Profile): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify({ ...readProfile(), ...profile }));
  } catch {
    /* private browsing */
  }
}

export function socketUrl(): string {
  const url = new URL(window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/ws';
  url.search = '';
  url.hash = '';
  return url.toString();
}
